import React, { useEffect, useState } from 'react';
import {
  Descriptions,
  Typography,
  notification,
  Input,
  Skeleton,
  Space,
  Card,
  Popconfirm,
  Form,
  Drawer,
  Table,
} from 'antd';

import BreadcrumbContainer from '../../containers/Breadcrumb';
import { CustomButton, CustomSelector } from '../../components';
import MESSAGES from '../../enums/Messages';
import INTERNAL_LINKS from '../../enums/InternalLinks';
import { Link } from 'react-router-dom';

import dayjs from 'dayjs';
import 'dayjs/locale/es';

import {
  getTDR as get,
  reactivate,
  confirm,
  getUsers,
  getRoles,
  assign,
  getDocuments,
} from '../../api/module/rejections';
import { catchException } from '../../utils/helpers/helpers';
import { generateTemplateURL } from '../../api/module/template';
import PdfViewer from '../../components/PdfViewer';

const { Title, Text } = Typography;
const { TextArea } = Input;

const breadcrumbItems = [
  {
    text: 'Rechazo de TDR',
    to: `${INTERNAL_LINKS.REJECT_TDR}`,
  },
  {
    text: 'Revisión',
  },
];

const initModalState = {
  pdf: undefined,
  open: false,
  key: 'no-key',
};

function DocumentTable({ contractData }) {
  const [documents, setDocuments] = useState();
  const [modalState, setModalState] = useState(initModalState);
  const [loadingPDF, setLoadingPDF] = useState(undefined);
  useEffect(() => {
    async function fetchData(id) {
      try {
        const res = await getDocuments(id);
        setDocuments(res.data.docs);
      } catch (err) {
        console.log(err);
        notification.error({
          message: MESSAGES.ERROR_MSG,
          description: MESSAGES.DESC_ERROR_GET_MSG,
          duration: 2,
        });
      }
    }
    fetchData(contractData.id);
  }, [contractData.id]);

  const columns = [
    {
      title: 'Tipo de documento',
      dataIndex: 'nombre_plantilla',
    },
    {
      title: 'Ver',
      key: 'actions',
      align: 'center',
      render: (_, record) => {
        return (
          <CustomButton
            type="default"
            text="Visualizar"
            htmlType="button"
            loading={loadingPDF === record.id}
            disabled={loadingPDF}
            onClick={async () => {
              setLoadingPDF(record.id);
              try {
                const response = await generateTemplateURL({
                  template: record.tipo_plantilla,
                  version: record.version,
                  person: contractData.tercero,
                  term: contractData.termino,
                });
                setModalState({
                  open: true,
                  pdf: response.data.url,
                  key: `pdf-${record.id}`,
                });
              } catch (err) {
                console.log(err);
                notification.error({
                  message: MESSAGES.ERROR_MSG,
                  description: MESSAGES.DESC_ERROR_GET_MSG,
                  duration: 2,
                });
              }
              setLoadingPDF(undefined);
            }}
          />
        );
      },
    },
  ];

  return (
    <>
      {documents ? (
        <div className="box-description">
          <Table
            dataSource={documents}
            columns={columns}
            bordered
            rowKey="id"
            pagination={false}
          />
          <PdfViewer
            pdf={modalState.pdf}
            open={modalState.open}
            onCancel={() => setModalState(initModalState)}
            isWord={true}
          />
        </div>
      ) : (
        <Skeleton active loading={true} />
      )}
    </>
  );
}

export default function RejectTDRForm({ match, history }) {
  const [validationData, setValidationData] = useState(undefined);

  const [loadingSend, setLoadingSend] = useState(false);
  const [showFix, setShowFix] = useState(false);
  const [loadingSendFix, setLoadingSendFix] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [formFix] = Form.useForm();

  async function fetchData() {
    try {
      const res = await get(match.params.validation_id);
      setValidationData(res.data);
    } catch (err) {
      console.log(err);
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_GET_MSG,
        duration: 2,
      });
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.params.validation_id]);

  const handleReactivation = async () => {
    setLoadingSend('reactivate');
    try {
      await reactivate(validationData.id);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: 'Rechazo de TDR reactivado correctamente.',
        duration: 2,
      });
      setTimeout(() => {
        history.push(
          `${INTERNAL_LINKS.REJECT_TDR}/pending/${validationData.proyecto_id}`,
        );
      }, MESSAGES.TIME);
    } catch (error) {
      setLoadingSend(false);
      catchException(error);
    }
  };

  const handleConfirmation = async () => {
    setLoadingSend('confirm');
    try {
      await confirm(validationData.id);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: 'Rechazo de TDR confirmado correctamente.',
        duration: 2,
      });
      setTimeout(() => {
        history.push(
          `${INTERNAL_LINKS.REJECT_TDR}/confirmed/${validationData.proyecto_id}`,
        );
      }, MESSAGES.TIME);
    } catch (error) {
      setLoadingSend(false);
      catchException(error);
    }
  };

  const handleFix = async (data) => {
    setLoadingSendFix(true);
    try {
      await assign(validationData.id, data);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: 'Nuevo TDR enviado correctamente.',
        duration: 2,
      });
      setTimeout(() => {
        history.push(
          `${INTERNAL_LINKS.REJECT_TDR}/pending/${validationData.proyecto_id}`,
        );
      }, MESSAGES.TIME);
    } catch (error) {
      setLoadingSendFix(false);
      catchException(error);
    }
  };

  return (
    <>
      <BreadcrumbContainer items={breadcrumbItems} />
      <Title className={'text-uppercase'} level={2}>
        Rechazo de TDR
      </Title>
      {validationData ? (
        <Space direction={'vertical'} style={{ width: '100%' }} size="large">
          <Descriptions layout="horizontal" column={2} bordered>
            <Descriptions.Item label="Proyecto a revisar" span={2}>
              {validationData.proyecto_nombre}
            </Descriptions.Item>
            <Descriptions.Item label="Nombre completo/Razón social" span={2}>
              {validationData.contratista_nombre}
            </Descriptions.Item>
            <Descriptions.Item label="Tipo de documento">
              {validationData.tipo_identificacion}
            </Descriptions.Item>
            <Descriptions.Item label="Número de documento">
              {validationData.identificacion}
            </Descriptions.Item>
            <Descriptions.Item label="Teléfono">
              {validationData.numero_celular}
            </Descriptions.Item>
            <Descriptions.Item label="Correo electrónico">
              {validationData.notificacion_email}
            </Descriptions.Item>
            <Descriptions.Item label="Cargo" span={2}>
              {validationData.cargo_nombre}
            </Descriptions.Item>
            <Descriptions.Item label="Tipo de contrato">
              {validationData.tipo_contrato}
            </Descriptions.Item>
            <Descriptions.Item label="Área de Corpoeducación">
              {validationData.area_corpoeducacion}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha de inicio">
              {dayjs(validationData.fecha_inicio).format('DD MMMM YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha de inicio máxima">
              {validationData.fecha_maxima_inicio
                ? dayjs(validationData.fecha_maxima_inicio).format(
                    'DD MMMM YYYY',
                  )
                : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha de finalización">
              {dayjs(validationData.fecha_fin).format('DD MMMM YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha de finalización máxima">
              {validationData.fecha_maxima_fin
                ? dayjs(validationData.fecha_maxima_fin).format('DD MMMM YYYY')
                : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Centro de costos">
              {validationData.centro_costo}
            </Descriptions.Item>
          </Descriptions>
          <Drawer
            title="Documentos enviados"
            placement="right"
            width={'40rem'}
            open={drawerOpen}
            maskClosable={false}
            onClose={() => setDrawerOpen(false)}
            mask={false}
          >
            <DocumentTable
              id={validationData.id}
              contractData={validationData}
            />
          </Drawer>
          <CustomButton
            htmlType="button"
            text="Ver documentos enviados"
            onClick={() => setDrawerOpen((s) => !s)}
          />
          <Descriptions layout="vertical" bordered>
            <Descriptions.Item label={'Motivo de Rechazo'}>
              {validationData.doc_motives.length > 0 ? (
                <>
                  <Title level={4}>POR DOCUMENTACIÓN</Title>
                  <ul>
                    {validationData.doc_motives.map((m) => (
                      <li>
                        <Text strong>{m.name}: </Text>
                        {m.motivo}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {validationData.auths_motives.length > 0 ? (
                <>
                  <Title level={5}>POR AUTORIZACIONES</Title>
                  <ul>
                    {validationData.auths_motives.map((m) => (
                      <li>
                        <div dangerouslySetInnerHTML={{ __html: m }} />
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {validationData.por_fecha ? (
                <>
                  <Title level={4}>OTROS MOTIVOS</Title>
                  <ul>
                    <li>Rechazo automático por fechas expiradas</li>
                  </ul>
                </>
              ) : null}
            </Descriptions.Item>
          </Descriptions>
          <div style={{ display: 'flex', justifyContent: 'space-around' }}>
            <CustomButton
              htmlType="button"
              type="default"
              text="Ajustar TDR"
              disabled={loadingSend || showFix}
              onClick={() => setShowFix(true)}
            />
            <Popconfirm
              placement="topLeft"
              title={
                <>
                  ¿Está seguro(a) de reactivar este TDR?
                  <br />
                  Ésta acción le permitirá al contratista ver nuevamente los TDR
                  para su aceptación.
                </>
              }
              okText="Sí"
              cancelText="No"
              onConfirm={handleReactivation}
            >
              <CustomButton
                htmlType="button"
                type="primary"
                text="Reactivar TDR"
                disabled={loadingSend || validationData.por_fecha}
                loading={loadingSend === 'reactivate'}
              />
            </Popconfirm>
            <Popconfirm
              placement="topLeft"
              title={
                <>
                  ¿Está seguro(a) de confirmar el rechazo?
                  <br />
                  Ésta acción cerrará definitivamente el proceso de contratación
                  de esta persona bajo este TDR.
                </>
              }
              okText="Sí"
              cancelText="No"
              onConfirm={handleConfirmation}
            >
              <CustomButton
                htmlType="button"
                type="danger"
                danger
                text="Confirmar rechazo"
                disabled={loadingSend}
                loading={loadingSend === 'confirm'}
              />
            </Popconfirm>
          </div>
          {showFix ? (
            <Card
              size={'small'}
              bordered={false}
              title={
                <Title level={3}>
                  Seleccione el rol, la persona, y digite la observación para
                  ajustar el TDR del contratista
                </Title>
              }
            >
              <Form
                layout="vertical"
                form={formFix}
                onFinish={handleFix}
                initialValues={{
                  identificador_interno: `RECHAZO ${validationData.identificacion} ${validationData.identificador_interno}`,
                }}
                onValuesChange={(data) => {
                  if (data.hasOwnProperty('rol')) {
                    formFix.setFieldsValue({ usuario_destino: undefined });
                  }
                }}
              >
                <Form.Item
                  label="Identificador Interno"
                  name="identificador_interno"
                  rules={[
                    {
                      required: true,
                      message: 'El campo Identificador interno es obligatorio.',
                    },
                    {
                      max: 80,
                      message:
                        'El identificador interno no puede tener mas de 80 caracteres',
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label="Selección el rol que revisará"
                  name="rol"
                  rules={[{ required: true }]}
                  valuePropName="initialValue"
                >
                  <CustomSelector
                    selectorProps={{
                      style: { width: '100%' },
                    }}
                    endpointFetch={getRoles}
                  />
                </Form.Item>
                <Form.Item noStyle dependencies={['rol']}>
                  {({ getFieldValue }) => {
                    const rol = getFieldValue('rol');
                    if (!rol) return null;
                    return (
                      <Form.Item
                        label="Selección la persona que revisará"
                        name="usuario_destino"
                        rules={[{ required: true }]}
                        valuePropName="initialValue"
                      >
                        <CustomSelector
                          selectorProps={{
                            style: { width: '100%' },
                          }}
                          endpointFetch={async () =>
                            await getUsers(validationData.proyecto_id, rol)
                          }
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
                <Form.Item
                  label="Digite la observación para ajustar el TDR para este contratista"
                  name="observacion"
                >
                  <TextArea />
                </Form.Item>
                <Form.Item>
                  <Popconfirm
                    placement="topLeft"
                    title={
                      <>
                        ¿Está seguro(a) de enviar el registro?
                        <br />
                        Lo que ud digite en la observación lo usará la persona a
                        la que envía para poder realizar el ajuste de TDR.
                      </>
                    }
                    okText="Sí"
                    cancelText="No"
                    onConfirm={() => formFix.submit()}
                    disabled={loadingSend}
                  >
                    <CustomButton
                      loading={loadingSendFix}
                      disabled={loadingSend}
                      htmlType="button"
                      text="Enviar"
                    />
                  </Popconfirm>
                </Form.Item>
              </Form>
            </Card>
          ) : null}
          <CustomButton
            type="default"
            htmlType="button"
            className={'back-button'}
            text={
              <Link
                to={`${INTERNAL_LINKS.REJECT_TDR}/pending/${validationData.proyecto_id}`}
              >
                Volver
              </Link>
            }
          />
        </Space>
      ) : (
        <Skeleton loading={true} />
      )}
    </>
  );
}
